import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@oysterjs/ui/button';
import { Input } from '@oysterjs/ui/input';
import {
  TriggerEmailRuleKind as Kind,
  TriggerTargetType,
  type CreateTriggerDefinitionInput,
  type CustomPresetFieldsFragment,
  type TriggerTargetInput,
  type TriggerEmailRuleInput
} from '~/api/graphql';
import {
  emptyEmailConfig,
  buildEmailCreateInput,
  validateEmailConfig,
  validateEmailAvailability,
  type EmailConfigDraft,
  type EmailErrors,
  type EmailOptions
} from '~/api/data/triggers/email-config';
import { CreationControls } from '~/components/triggers/email-builder/creation-controls';
import { CreationSectionCard } from '~/components/triggers/email-builder/creation-card';
import {
  CreationWhen,
  type FolderCatalog
} from '~/components/triggers/email-builder/creation-when';
import {
  CreationRuleEditor,
  CreationGroupsJoin
} from '~/components/triggers/email-builder/creation-rule';
import {
  CreationThen,
  validateCreationSource,
  type CreationSource
} from '~/components/triggers/email-builder/creation-then';
import { CreationReview } from '~/components/triggers/email-builder/creation-review';
import { BuilderField, PathErrors, Help } from '~/components/triggers/email-builder/fields';
import { AGENCY_TIMEZONE_OPTIONS } from '~/lib/agency-timezones';
import {
  CreationEventContext,
  CreationEventField,
  useCreationEventGuard,
  useCreationNameError
} from '~/components/triggers/email-builder/creation-event';
import '~/components/triggers/email-builder/creation-editor.css';

export type EmailCreationDraft = {
  name: string;
  config: EmailConfigDraft;
  target: TriggerTargetInput;
};
export type EmailSaveOutcome = { errors?: EmailErrors; message?: string; uncertain?: boolean };
const containsTime = (rule?: TriggerEmailRuleInput | null): boolean =>
  !!rule && (rule.kind === Kind.Time || (rule.children ?? []).some(containsTime));
export const emptyEmailCreationDraft = (): EmailCreationDraft => ({
  name: '',
  config: emptyEmailConfig(),
  target: { type: TriggerTargetType.WorkspaceRun, prompt: '' }
});

// Editing reopens a saved prompt-only target on Prompt. Creating always starts on
// Preset, so a prompt-only trigger is an explicit choice even with instructions typed.
const initialSource = (target?: TriggerTargetInput): CreationSource =>
  target && !target.presetId && !target.preset && target.prompt ? 'prompt' : 'preset';

// Enter in a text box must not submit: saving starts monitoring at once, so only the
// Create or Save button saves. Text areas and buttons keep their own Enter.
const blockEnterSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
  const target = event.target;
  if (
    event.key === 'Enter' &&
    target instanceof HTMLInputElement &&
    !['submit', 'image', 'button', 'reset'].includes(target.type)
  ) {
    event.preventDefault();
  }
};

export const EmailBuilderForm = ({
  options,
  folders,
  customPresets = [],
  hasProAccess = false,
  initial,
  onSave,
  canCreate = true,
  optionsReady = true,
  onConfigChange,
  mode = 'create'
}: {
  options: EmailOptions;
  folders: Readonly<Record<string, FolderCatalog>>;
  customPresets?: CustomPresetFieldsFragment[];
  hasProAccess?: boolean;
  initial?: EmailCreationDraft;
  onSave: (input: CreateTriggerDefinitionInput) => Promise<EmailSaveOutcome>;
  /** Whether the viewer may save: create permission, or update permission when editing. */
  canCreate?: boolean;
  optionsReady?: boolean;
  onConfigChange?: (config: EmailConfigDraft) => void;
  /** Edit reopens a saved trigger: same editor, but saving updates it in place. */
  mode?: 'create' | 'edit';
}) => {
  const editing = mode === 'edit';
  const startSource = editing ? initialSource(initial?.target) : 'preset';
  const [draft, setDraft] = React.useState(initial ?? emptyEmailCreationDraft);
  const [source, setSource] = React.useState<CreationSource>(startSource);
  const [errors, setErrors] = React.useState<EmailErrors>({});
  const [message, setMessage] = React.useState('');
  const [review, setReview] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [uncertain, setUncertain] = React.useState(false);
  const saveLock = React.useRef(false);
  const alert = React.useRef<HTMLDivElement>(null);
  const timezoneId = React.useId();
  const config = draft.config;
  const saveEnabled = canCreate && optionsReady;
  const creation = React.useContext(CreationEventContext);
  const name = creation ? creation.name : draft.name;
  useCreationNameError(creation ? errors.name : undefined);
  const baseline = initial ?? emptyEmailCreationDraft();
  useCreationEventGuard(
    JSON.stringify({ ...draft, name: baseline.name }) !== JSON.stringify(baseline) ||
      source !== startSource,
    saving || uncertain
  );
  React.useEffect(() => {
    onConfigChange?.(config);
  }, [config, onConfigChange]);
  const setConfig = (next: EmailConfigDraft) =>
    setDraft((current) => ({ ...current, config: next }));
  const setRule = (rule: TriggerEmailRuleInput | null) => setConfig({ ...config, rule });
  const folderData = Object.fromEntries(
    Object.entries(folders).map(([id, folder]) => [
      id,
      folder.loading || folder.error ? undefined : folder.data
    ])
  );
  const validate = (): EmailErrors => {
    const nextErrors: EmailErrors = {
      ...validateEmailConfig(config),
      ...validateEmailAvailability(config, draft.target, options, folderData),
      ...validateCreationSource(draft.target, source)
    };
    if (!name.trim()) nextErrors.name = 'Enter a trigger name.';
    return nextErrors;
  };
  // Once a save is refused, the highlighted settings follow the draft: a
  // setting the user fixes stops being flagged, and the summary goes once
  // nothing is left, rather than waiting for the next save attempt.
  const [checking, setChecking] = React.useState(false);
  const liveErrors = checking ? JSON.stringify(validate()) : '';
  React.useEffect(() => {
    if (!checking) return;
    const next = JSON.parse(liveErrors) as EmailErrors;
    setErrors(next);
    if (Object.keys(next).length === 0) {
      setChecking(false);
      setMessage('');
    }
  }, [checking, liveErrors]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saveLock.current || !saveEnabled || uncertain) return;
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setChecking(true);
      setMessage('Check the highlighted settings. Your draft has not been saved.');
      requestAnimationFrame(() => alert.current?.focus());
      return;
    }
    saveLock.current = true;
    setSaving(true);
    setMessage('');
    try {
      const outcome = await onSave(buildEmailCreateInput(name, config, draft.target));
      setErrors(outcome.errors ?? {});
      setMessage(outcome.message ?? '');
      if (outcome.uncertain) setUncertain(true);
    } catch {
      setUncertain(true);
      setMessage(
        'The save result is unknown. Check the trigger list before trying again; this editor will not replay the save. Your draft is preserved.'
      );
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };
  return (
    <CreationControls.Provider value={true}>
      <form
        className="space-y-6"
        onSubmit={(event) => void save(event)}
        onKeyDown={blockEnterSubmit}
        noValidate
        aria-label={
          editing
            ? 'Edit connected-mailbox email trigger'
            : 'Create connected-mailbox email trigger'
        }
      >
        {!creation && (
          <div className="rounded-lg bg-white shadow dark:bg-neutral-800">
            <div className="px-4 py-5 sm:px-6">
              <BuilderField
                label="Trigger name"
                path="name"
                errors={errors}
                description={
                  editing
                    ? undefined
                    : 'A short label shown in the triggers list. Starts monitoring on creation.'
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    className="max-w-md"
                    value={draft.name}
                    disabled={saving}
                    onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  />
                )}
              </BuilderField>
            </div>
          </div>
        )}
        {!canCreate && (
          <p role="alert">
            {editing
              ? 'You do not have permission to edit mailbox triggers.'
              : 'You do not have permission to create mailbox triggers.'}
          </p>
        )}
        {canCreate && !optionsReady && (
          <p role="status">
            Saving is unavailable until mailbox and preset options are ready. Your draft is
            preserved.
          </p>
        )}
        {(message || Object.keys(errors).length > 0) && (
          <div
            ref={alert}
            role="alert"
            tabIndex={-1}
            className="my-4 text-red-600 dark:text-red-400"
          >
            {message || 'Check the highlighted settings.'}
            <ul>
              {Object.entries(errors).map(([path, error]) => (
                <li key={path}>{error}</li>
              ))}
            </ul>
            {uncertain && <Link to="/app/workflows/triggers">Check the trigger list</Link>}
          </div>
        )}
        <fieldset
          disabled={saving || !saveEnabled || uncertain}
          className="email-creation-flow flex flex-col gap-6"
        >
          <CreationSectionCard step="when">
            <CreationWhen
              eventField={saveEnabled && !editing ? <CreationEventField /> : undefined}
              config={config}
              onChange={setConfig}
              options={options}
              catalogs={folders}
              errors={errors}
            />
          </CreationSectionCard>
          <CreationSectionCard
            step="if"
            aside={<CreationGroupsJoin rule={config.rule} onChange={setRule} />}
          >
            <CreationRuleEditor
              rule={config.rule}
              onChange={setRule}
              options={options}
              errors={errors}
            />
            {(containsTime(config.rule) || config.timezone || errors.timezone) && (
              <div className="mt-4">
                <BuilderField
                  label="Time zone"
                  path="timezone"
                  errors={errors}
                  description="Choose a time zone to save with this trigger. It is never inferred from your browser or defaulted to UTC; daylight saving follows your chosen zone."
                >
                  {(props) => (
                    <Input
                      {...props}
                      list={timezoneId}
                      value={config.timezone ?? ''}
                      placeholder="Choose or enter a time zone"
                      onChange={(event) =>
                        setConfig({ ...config, timezone: event.target.value || null })
                      }
                    />
                  )}
                </BuilderField>
                <datalist id={timezoneId}>
                  {AGENCY_TIMEZONE_OPTIONS.map((zone) => (
                    <option key={zone.value} value={zone.value}>
                      {zone.label}
                    </option>
                  ))}
                </datalist>
              </div>
            )}
            <PathErrors path="rule" errors={errors} />
          </CreationSectionCard>
          <CreationSectionCard step="then">
            <CreationThen
              target={draft.target}
              onChange={(target) => setDraft({ ...draft, target })}
              source={source}
              onSourceChange={setSource}
              privacy={config.privacy}
              onPrivacyChange={(privacy) => setConfig({ ...config, privacy })}
              options={options}
              customPresets={customPresets}
              hasProAccess={hasProAccess}
              errors={errors}
            />
          </CreationSectionCard>
        </fieldset>
        <Help>
          {editing
            ? 'Changes apply to emails that arrive after you save; emails already handled are not re-evaluated. Pause and Resume remain separate actions.'
            : 'Creating this trigger starts monitoring immediately. Only future eligible arrivals are considered; Pause and Resume remain separate actions on an existing trigger.'}
        </Help>
        <div className="flex justify-end gap-3">
          <Button color="white" type="button" onClick={() => setReview(true)}>
            Review trigger
          </Button>
          <Button color="white" href="/app/workflows/triggers" disabled={saving}>
            Cancel
          </Button>
          <Button
            color="sky"
            type="submit"
            loading={saving}
            disabled={saving || !saveEnabled || uncertain}
          >
            {editing
              ? saving
                ? 'Saving…'
                : 'Save changes'
              : saving
                ? 'Creating…'
                : 'Create trigger'}
          </Button>
        </div>
      </form>
      <CreationReview
        open={review}
        onClose={() => setReview(false)}
        config={config}
        target={draft.target}
        options={options}
      />
    </CreationControls.Provider>
  );
};
